import { useCallback, useEffect, useRef, useState } from "react"

import PinKeypad from "@/components/keyless-portal/PinKeypad"
import VehicleControls from "@/components/keyless-portal/VehicleControls"
import { PIN_LENGTH } from "@/components/keyless-portal/constants"
import "@/components/keyless-portal/keyless.css"
import { type VehicleCommand, useKeylessPortal } from "@/hooks/useKeylessPortal"

const ERROR_CLEAR_DELAY_MS = 900
const TOAST_DURATION_MS = 2500

/**
 * Public PIN portal, replacing hived-keyless.vercel.app. Deliberately unchanged for the
 * driver: enter a PIN, see the van, lock or unlock. The difference is that the Make webhooks
 * are now called by an edge function rather than by this page.
 */
const KeylessPortal = () => {
  const { vehicle, validatePin, sendCommand, refreshVehicle, signOut } = useKeylessPortal()
  const [pin, setPin] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [isValidating, setIsValidating] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const submittedPinRef = useRef<string | null>(null)

  useEffect(() => {
    if (!errorMessage) return

    const timer = setTimeout(() => {
      setPin("")
      setErrorMessage("")
      submittedPinRef.current = null
    }, ERROR_CLEAR_DELAY_MS)

    return () => clearTimeout(timer)
  }, [errorMessage])

  useEffect(() => {
    if (!toastMessage) return

    const timer = setTimeout(() => setToastMessage(""), TOAST_DURATION_MS)

    return () => clearTimeout(timer)
  }, [toastMessage])

  // Submits itself on the fourth digit, so there's no confirm button to press. The PIN is
  // recorded before the request so a re-render can't submit it twice - every attempt is
  // counted against a shared rate limit, and a success leaves the pin at full length.
  useEffect(() => {
    if (pin.length !== PIN_LENGTH || submittedPinRef.current === pin) return

    submittedPinRef.current = pin

    const submitPin = async () => {
      setIsValidating(true)
      try {
        await validatePin(pin)
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Connection error. Please try again.",
        )
      } finally {
        setIsValidating(false)
      }
    }

    void submitPin()
  }, [pin, validatePin])

  const handleDigit = useCallback((digit: string) => {
    setPin((current) => (current.length >= PIN_LENGTH ? current : `${current}${digit}`))
  }, [])

  const handleDelete = useCallback(() => {
    setErrorMessage("")
    setPin((current) => current.slice(0, -1))
    submittedPinRef.current = null
  }, [])

  // Physical keyboards should work as well as the on-screen keypad, but only while the
  // keypad is the thing on screen.
  useEffect(() => {
    if (vehicle) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault()
        handleDigit(event.key)
        return
      }

      if (event.key === "Backspace" || event.key === "Delete") {
        event.preventDefault()
        handleDelete()
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [vehicle, handleDigit, handleDelete])

  const handleCommand = async (command: VehicleCommand) => {
    setIsSending(true)
    setToastMessage("Sending command…")

    try {
      await sendCommand(command)
      setToastMessage(
        command === "lock-and-disable-driving" ? "🔒 Vehicle locked" : "🔓 Vehicle unlocked",
      )
    } catch (error) {
      setToastMessage(
        error instanceof Error ? error.message : "Connection error. Please try again.",
      )
    } finally {
      setIsSending(false)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)

    try {
      await refreshVehicle()
      setToastMessage("↻ Status updated")
    } catch (error) {
      setToastMessage(
        error instanceof Error ? error.message : "Connection error. Please try again.",
      )
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSignOut = () => {
    setPin("")
    setErrorMessage("")
    submittedPinRef.current = null
    signOut()
  }

  return (
    <div className="keyless-portal">
      <div className="app">
        <div className="topbar">
          <div className="logo">
            H<span className="logo-bar" />
            VED
          </div>
          <div className="badge">Driver portal</div>
        </div>

        {vehicle ? (
          <VehicleControls
            isRefreshing={isRefreshing}
            isSending={isSending}
            onCommand={handleCommand}
            onRefresh={handleRefresh}
            onSignOut={handleSignOut}
            vehicle={vehicle}
          />
        ) : (
          <PinKeypad
            errorMessage={errorMessage}
            isValidating={isValidating}
            onDelete={handleDelete}
            onDigit={handleDigit}
            pin={pin}
          />
        )}
      </div>

      <div className={toastMessage ? "toast show" : "toast"}>{toastMessage}</div>
    </div>
  )
}

export default KeylessPortal
